<template>
  <div class="generic-duplication-modal modal-card">
    <header class="generic-duplication-modal__header">
      <div>
        <p class="generic-duplication-modal__eyebrow">{{ internalMediaLabel }}</p>
        <h3>Dupliquer chez des clients</h3>
        <p>{{ content.title }}</p>
      </div>
      <button type="button" aria-label="Fermer" @click="close">
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
    </header>

    <section class="generic-duplication-modal__body">
      <div class="generic-duplication-modal__toolbar">
        <label class="generic-duplication-modal__search">
          <i class="fas fa-search" aria-hidden="true"></i>
          <input
            v-model.trim="search"
            type="search"
            placeholder="Rechercher un domaine"
            aria-label="Rechercher un domaine destinataire"
          >
        </label>
        <span v-if="selectedRows.length" class="generic-duplication-modal__selection-count">
          {{ selectedRows.length }} domaine{{ selectedRows.length > 1 ? 's' : '' }} sélectionné{{ selectedRows.length > 1 ? 's' : '' }}
        </span>
      </div>

      <p v-if="loading" class="generic-duplication-modal__state">
        <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Chargement des domaines…
      </p>
      <p v-else-if="loadError" class="generic-duplication-modal__state generic-duplication-modal__state--error">
        {{ loadError }}
      </p>
      <p v-else-if="!domains.length" class="generic-duplication-modal__state">
        Aucun domaine client disponible.
      </p>
      <div v-else class="generic-duplication-modal__table-scroll">
        <table>
          <thead>
            <tr>
              <th class="generic-duplication-modal__check">
                <input
                  type="checkbox"
                  :checked="allFilteredSelected"
                  :disabled="!filteredDomains.length || submitting"
                  aria-label="Sélectionner tous les domaines affichés"
                  @change="toggleAllFiltered($event.target.checked)"
                >
              </th>
              <th>Domaine</th>
              <th>Statut</th>
              <th>{{ isSurvey ? 'Date de début' : 'Date' }}</th>
              <th v-if="isSurvey">Date de fin</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="domain in filteredDomains" :key="domain.uuid">
              <td class="generic-duplication-modal__check">
                <input
                  type="checkbox"
                  :checked="isSelected(domain.uuid)"
                  :disabled="submitting"
                  :aria-label="`Sélectionner ${domain.label}`"
                  @change="toggleDomain(domain.uuid, $event.target.checked)"
                >
              </td>
              <td>
                <strong>{{ domain.label }}</strong>
                <span>{{ domain.application.label || '—' }}</span>
                <span
                  v-if="domain.duplicated_at"
                  class="generic-duplication-modal__already"
                  :title="`Ce contenu a déjà été dupliqué chez ${domain.label}`"
                >
                  <i class="fas fa-check" aria-hidden="true"></i>
                  Déjà dupliqué le {{ formatDuplicatedAt(domain.duplicated_at) }}
                </span>
              </td>
              <td>
                <select
                  v-if="isSelected(domain.uuid)"
                  v-model="selections[domain.uuid].status"
                  :disabled="submitting"
                  :aria-label="`Statut pour ${domain.label}`"
                >
                  <option value="draft">Brouillon</option>
                  <option value="active">Actif</option>
                </select>
                <span v-else class="generic-duplication-modal__disabled-value">—</span>
              </td>
              <td>
                <input
                  v-if="isSelected(domain.uuid)"
                  v-model="selections[domain.uuid].publicationAt"
                  type="datetime-local"
                  :disabled="submitting"
                  :aria-label="`${isSurvey ? 'Date de début' : 'Date'} pour ${domain.label}`"
                >
                <span v-else class="generic-duplication-modal__disabled-value">—</span>
              </td>
              <td v-if="isSurvey">
                <input
                  v-if="isSelected(domain.uuid)"
                  v-model="selections[domain.uuid].endAt"
                  type="datetime-local"
                  :min="selections[domain.uuid].publicationAt"
                  :disabled="submitting"
                  :aria-label="`Date de fin pour ${domain.label}`"
                >
                <span v-else class="generic-duplication-modal__disabled-value">—</span>
              </td>
            </tr>
            <tr v-if="!filteredDomains.length">
              <td :colspan="isSurvey ? 5 : 4" class="generic-duplication-modal__empty">
                Aucun domaine ne correspond à la recherche.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p class="generic-duplication-modal__hint">
        Chaque copie est créée avec le statut et {{ isSurvey ? 'la période' : 'la date' }} indiqués sur sa ligne.
      </p>
      <p v-if="validationMessage" class="generic-duplication-modal__validation">
        {{ validationMessage }}
      </p>
    </section>

    <footer class="generic-duplication-modal__footer">
      <button type="button" class="generic-modal-button" :disabled="submitting" @click="close">Annuler</button>
      <button
        type="button"
        class="generic-modal-button generic-modal-button--primary"
        :disabled="!canSubmit || submitting"
        @click="duplicate"
      >
        <i v-if="submitting" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
        {{ submitting ? 'Duplication…' : duplicateButtonLabel }}
      </button>
    </footer>
  </div>
</template>

<script>
import { addDays, format } from 'date-fns'
import { http } from '@/services/api.service'
import { INTERNAL_MEDIA_LABEL } from '@/utils/internalMediaLabel'

export default {
  name: 'generic-content-duplication-modal',
  props: {
    content: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      domains: [],
      loading: true,
      loadError: '',
      search: '',
      selections: {},
      submitting: false
    }
  },
  computed: {
    internalMediaLabel () {
      return INTERNAL_MEDIA_LABEL
    },
    isSurvey () {
      return this.content.type === 'survey'
    },
    filteredDomains () {
      const search = this.search.toLocaleLowerCase('fr')
      if (!search) return this.domains
      return this.domains.filter(domain => {
        const haystack = `${domain.label} ${domain.application?.label || ''}`.toLocaleLowerCase('fr')
        return haystack.includes(search)
      })
    },
    selectedRows () {
      return this.domains
        .filter(domain => this.isSelected(domain.uuid))
        .map(domain => ({ domain, ...this.selections[domain.uuid] }))
    },
    allFilteredSelected () {
      return Boolean(this.filteredDomains.length) && this.filteredDomains.every(domain => this.isSelected(domain.uuid))
    },
    alreadyDuplicatedRows () {
      return this.selectedRows.filter(row => row.domain.duplicated_at)
    },
    validationMessage () {
      if (!this.selectedRows.length) return ''
      if (this.selectedRows.some(row => !row.publicationAt)) {
        return `Renseignez ${this.isSurvey ? 'la date de début' : 'la date'} pour chaque domaine sélectionné.`
      }
      if (this.isSurvey && this.selectedRows.some(row => !row.endAt)) {
        return 'Renseignez la date de fin pour chaque domaine sélectionné.'
      }
      if (this.isSurvey && this.selectedRows.some(row => new Date(row.endAt) <= new Date(row.publicationAt))) {
        return 'Chaque date de fin doit être postérieure à sa date de début.'
      }
      if (this.isSurvey && this.selectedRows.some(row => row.status === 'active' && new Date(row.endAt) < new Date())) {
        return 'La date de fin doit être à venir pour chaque sondage actif.'
      }
      return ''
    },
    canSubmit () {
      return this.selectedRows.length > 0 && !this.validationMessage
    },
    duplicateButtonLabel () {
      if (!this.selectedRows.length) return 'Dupliquer'
      return `Dupliquer chez ${this.selectedRows.length} client${this.selectedRows.length > 1 ? 's' : ''}`
    }
  },
  created () {
    this.loadDomains()
  },
  methods: {
    async loadDomains () {
      this.loading = true
      this.loadError = ''
      try {
        const { data } = await http.get('/internal/generic-contents/client-domains', {
          params: {
            content_type: this.content.type,
            content_uuid: this.content.uuid
          }
        })
        this.domains = Array.isArray(data) ? data : []
      } catch (error) {
        this.loadError = 'Impossible de charger les domaines clients.'
      } finally {
        this.loading = false
      }
    },
    defaultSelection () {
      const now = new Date()
      now.setSeconds(0, 0)
      return {
        status: 'draft',
        publicationAt: format(now, "yyyy-MM-dd'T'HH:mm"),
        endAt: this.isSurvey ? format(addDays(now, 7), "yyyy-MM-dd'T'HH:mm") : null
      }
    },
    isSelected (domainUuid) {
      return Object.prototype.hasOwnProperty.call(this.selections, domainUuid)
    },
    toggleDomain (domainUuid, selected) {
      if (selected) this.$set(this.selections, domainUuid, this.defaultSelection())
      else this.$delete(this.selections, domainUuid)
    },
    toggleAllFiltered (selected) {
      this.filteredDomains.forEach(domain => this.toggleDomain(domain.uuid, selected))
    },
    apiDate (value) {
      const normalized = String(value || '').replace('T', ' ')
      return normalized.length === 16 ? `${normalized}:00` : normalized
    },
    formatDuplicatedAt (value) {
      const date = new Date(String(value).replace(' ', 'T'))
      return Number.isNaN(date.getTime()) ? '—' : format(date, 'dd/MM/yyyy à HH:mm')
    },
    escapeHtml (value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    duplicate () {
      if (!this.canSubmit || this.submitting) return
      if (!this.alreadyDuplicatedRows.length) {
        this.submit(false)
        return
      }

      // Second confirmation when at least one selected domain already received the content.
      const lines = this.alreadyDuplicatedRows
        .map(row => `• ${this.escapeHtml(row.domain.label)} (le ${this.formatDuplicatedAt(row.domain.duplicated_at)})`)
        .join('<br>')
      const several = this.alreadyDuplicatedRows.length > 1
      this.$buefy.dialog.confirm({
        title: 'Contenu déjà dupliqué',
        message: `Vous avez déjà dupliqué ce contenu chez ${several ? 'ces clients' : 'ce client'} :<br>${lines}<br><br>` +
          `Une nouvelle copie sera créée chez ${several ? 'chacun d’eux' : 'lui'}. Voulez-vous continuer ?`,
        confirmText: 'Dupliquer à nouveau',
        cancelText: 'Annuler',
        type: 'is-warning',
        hasIcon: true,
        onConfirm: () => this.submit(true)
      })
    },
    async submit (confirmAlreadyDuplicated) {
      if (this.submitting) return
      this.submitting = true
      try {
        const targets = this.selectedRows.map(row => ({
          domain_uuid: row.domain.uuid,
          status: row.status,
          publication_at: this.apiDate(row.publicationAt),
          ...(this.isSurvey ? { end_at: this.apiDate(row.endAt) } : {})
        }))
        const { data } = await http.post(
          `/internal/generic-contents/media/${this.content.type}/${this.content.uuid}/duplicate`,
          {
            targets,
            ...(confirmAlreadyDuplicated ? { confirm_already_duplicated: true } : {})
          }
        )
        this.$emit('duplicated', data)
        this.$notify({
          group: 'maviepro-success',
          text: `Le contenu a été dupliqué chez ${targets.length} client${targets.length > 1 ? 's' : ''}.`
        })
        this.close()
      } catch (error) {
        const message = error?.response?.data?.message || 'Impossible de dupliquer ce contenu.'
        this.$notify({ group: 'maviepro-error', text: message })
      } finally {
        this.submitting = false
      }
    },
    close () {
      if (this.$parent && typeof this.$parent.close === 'function') this.$parent.close()
    }
  }
}
</script>

<style scoped>
.generic-duplication-modal { background: #fff; border-radius: 14px; display: flex; flex-direction: column; max-height: calc(100vh - 48px); max-width: calc(100vw - 32px); overflow: hidden; width: 1080px; }
.generic-duplication-modal__header { align-items: flex-start; border-bottom: 1px solid #e5e7eb; display: flex; flex: 0 0 auto; justify-content: space-between; padding: 22px 26px; }
.generic-duplication-modal__header h3 { color: #111827; font-size: 22px; font-weight: 700; margin: 2px 0 4px; }
.generic-duplication-modal__header p:last-child { color: #64748b; margin: 0; }
.generic-duplication-modal__header > button { background: transparent; border: 0; color: #9ca3af; font-size: 18px; }
.generic-duplication-modal__eyebrow { color: #2563eb !important; font-size: 12px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; }
.generic-duplication-modal__body { display: flex; flex: 1 1 auto; flex-direction: column; gap: 12px; min-height: 0; padding: 22px 26px; }
.generic-duplication-modal__toolbar { align-items: center; display: flex; gap: 16px; justify-content: space-between; }
.generic-duplication-modal__search { align-items: center; border: 1px solid #d1d5db; border-radius: 9px; display: flex; max-width: 420px; padding: 0 12px; width: 100%; }
.generic-duplication-modal__search:focus-within { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, .12); }
.generic-duplication-modal__search i { color: #9ca3af; }
.generic-duplication-modal__search input { border: 0; box-shadow: none; outline: none; padding: 11px; width: 100%; }
.generic-duplication-modal__selection-count { color: #475569; font-size: 13px; font-weight: 600; white-space: nowrap; }
.generic-duplication-modal__table-scroll { border: 1px solid #e5e7eb; border-radius: 10px; flex: 1 1 auto; min-height: 180px; overflow: auto; }
.generic-duplication-modal table { border-collapse: separate; border-spacing: 0; min-width: 820px; table-layout: fixed; width: 100%; }
.generic-duplication-modal th { background: #f8fafc; color: #4b5563; font-size: 11px; letter-spacing: .04em; padding: 11px 12px; position: sticky; text-align: left; text-transform: uppercase; top: 0; z-index: 1; }
.generic-duplication-modal th:first-child { width: 48px; }
.generic-duplication-modal th:nth-child(2) { width: 28%; }
.generic-duplication-modal th:nth-child(3) { width: 150px; }
.generic-duplication-modal td { border-top: 1px solid #edf0f3; color: #374151; height: 62px; padding: 9px 12px; vertical-align: middle; }
.generic-duplication-modal td strong, .generic-duplication-modal td span { display: block; }
.generic-duplication-modal td strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.generic-duplication-modal td > span:not(.generic-duplication-modal__disabled-value) { color: #64748b; font-size: 12px; margin-top: 2px; }
.generic-duplication-modal td > span.generic-duplication-modal__already { align-items: center; color: #b45309; display: inline-flex; font-size: 11px; font-weight: 600; gap: 4px; margin-top: 4px; white-space: nowrap; }
.generic-duplication-modal td select, .generic-duplication-modal td input[type="datetime-local"] { border: 1px solid #d1d5db; border-radius: 7px; min-height: 38px; padding: 7px 9px; width: 100%; }
.generic-duplication-modal__check { text-align: center !important; }
.generic-duplication-modal__check input { accent-color: #2563eb; height: 16px; width: 16px; }
.generic-duplication-modal__disabled-value { color: #94a3b8; text-align: center; }
.generic-duplication-modal__hint, .generic-duplication-modal__state, .generic-duplication-modal__validation { color: #64748b; font-size: 12px; margin: 0; }
.generic-duplication-modal__state { padding: 52px 20px; text-align: center; }
.generic-duplication-modal__state--error, .generic-duplication-modal__validation { color: #b91c1c; }
.generic-duplication-modal__empty { color: #64748b !important; padding: 36px !important; text-align: center; }
.generic-duplication-modal__footer { background: #f8fafc; border-top: 1px solid #e5e7eb; display: flex; flex: 0 0 auto; gap: 10px; justify-content: flex-end; padding: 17px 26px; }
.generic-modal-button { background: #fff; border: 1px solid #d1d5db; border-radius: 8px; color: #374151; font-weight: 600; padding: 9px 16px; }
.generic-modal-button--primary { background: #2563eb; border-color: #2563eb; color: #fff; }
.generic-modal-button:disabled { cursor: not-allowed; opacity: .55; }
@media (max-width: 760px) {
  .generic-duplication-modal__toolbar { align-items: stretch; flex-direction: column; }
  .generic-duplication-modal__selection-count { white-space: normal; }
}
</style>
